import React, { FC, useState, useRef } from 'react';
import translate from '@translate';
import { checkFormValidity } from 'src/utils/formFunctions';
import { TransportPackageType } from 'src/types/transport';
import TextField from 'src/components/TextField';
import Select from 'src/components/Select';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { sizes } from 'src/themes/default';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { FormErrorsType } from '../../types/global';

export type EditKeysType =
    | 'WEIGHT'
    | 'WIDTH'
    | 'HEIGHT'
    | 'LENGTH'
    | 'PRICE'
    | 'EXPEDITION_BOX_NUMBER'
    | 'PACKAGE_TYPE'
    | 'VOLUME'
    | 'LOADING_METERS';

const initData: TransportPackageType = {
    LINE_STATE: 'E',
};
type NewPackageBlockPropsType = {
    data?: TransportPackageType[];
    onChange: (value: TransportPackageType[]) => void;
    packageTypeCodes: ConstantsCodeType[];
};

const NewPackageBlock: FC<NewPackageBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<TransportPackageType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: unknown, error: boolean) => {
        const newData = { ...editData };
        newData[key] = value as never;
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item) => item.ID || 0)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId,
                },
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3">
                {translate('transportDetail.addPackage')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={2}>
                        <Select
                            options={props.packageTypeCodes}
                            value={editData.PACKAGE_TYPE}
                            error={editErrors.PACKAGE_TYPE}
                            onChange={(value, error) => changeValue('PACKAGE_TYPE', value, error)}
                            name="PACKAGE_TYPE"
                            label={translate('transportDetail.packageTable.packageType')}
                            fullWidth
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.WEIGHT}
                            error={editErrors.WEIGHT}
                            onChange={(value, error) => changeValue('WEIGHT', value, error)}
                            name="WEIGHT"
                            label={translate('transportDetail.packageTable.weight')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.VOLUME}
                            error={editErrors.VOLUME}
                            onChange={(value, error) => changeValue('VOLUME', value, error)}
                            name="VOLUME"
                            label={translate('transportDetail.packageTable.volume')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.LOADING_METERS}
                            error={editErrors.LOADING_METERS}
                            onChange={(value, error) => changeValue('LOADING_METERS', value, error)}
                            name="LOADING_METERS"
                            label={translate('transportDetail.packageTable.loadingMeters')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.WIDTH}
                            error={editErrors.WIDTH}
                            onChange={(value, error) => changeValue('WIDTH', value, error)}
                            name="WIDTH"
                            label={translate('transportDetail.packageTable.width')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.HEIGHT}
                            error={editErrors.HEIGHT}
                            onChange={(value, error) => changeValue('HEIGHT', value, error)}
                            name="HEIGHT"
                            label={translate('transportDetail.packageTable.height')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.LENGTH}
                            error={editErrors.LENGTH}
                            onChange={(value, error) => changeValue('LENGTH', value, error)}
                            name="LENGTH"
                            label={translate('transportDetail.packageTable.length')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.PRICE}
                            error={editErrors.PRICE}
                            onChange={(value, error) => changeValue('PRICE', value, error)}
                            name="PRICE"
                            label={translate('transportDetail.packageTable.price')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button onClick={handleAddRow} sx={{ marginTop: sizes.input_top_PD }} mobileFullWidht>
                            + {translate('returnDetail.productTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewPackageBlock;
